import { useEffect, useState, useCallback } from 'react';
import { api } from '@/services/api';

/**
 * DiagnosticsView — eight observability panels.
 *
 * Embedded in Mission Control (DIAGNOSTICS tab) and in the standalone
 * /observability page. All endpoints accept owner JWT or X-Service-Key.
 */

type Status = 'pass' | 'fail' | 'degraded' | 'skip' | 'unknown';

interface ProbeReport {
  available?: boolean;
  total?: number;
  pass?: number;
  fail?: number;
  skip?: number;
  completed_at?: string;
  probes?: Array<{
    name: string;
    status: Status;
    latency_ms?: number;
    detail?: string;
    checked_at?: string;
  }>;
}

interface SelfHealth {
  generated_at?: string;
  overall?: Status;
  summary?: string;
  components?: Record<string, { status: Status; detail: string }>;
  failing?: Array<{ name: string; detail: string }>;
  degraded?: Array<{ name: string; detail: string }>;
}

interface PersonaReport {
  available?: boolean;
  total?: number;
  passed?: number;
  failed?: number;
  completed_at?: string;
  scenarios?: Array<{
    id: string;
    pass: boolean;
    failures: string[];
    response_text: string;
    tools_fired: string[];
    latency_ms?: number;
  }>;
}

interface RoutingResponse {
  count: number;
  corrections: Array<{
    scenario_id: string;
    prompt: string;
    expected_tools: string[];
    last_actual_tools?: string[];
    fail_count?: number;
  }>;
  system_prompt_block: string;
}

interface SentinelCalibration {
  window_days: number;
  fires_total: number;
  fires_per_day: number;
  fires_by_check: Record<string, number>;
  false_positives: number;
  false_positive_rate: number;
  acknowledged: number;
  acknowledgement_rate: number;
  verdict: 'well_tuned' | 'noisy' | 'too_quiet';
}

interface CalibrationConfirmResult {
  status?: string;
  version?: string;
  confirmed?: boolean;
  n?: number;
  confirmed_by?: string | null;
  confirmed_at?: string | null;
  detail?: string;
}

interface VisionReading {
  available?: boolean;
  ok?: boolean;
  scene?: string;
  people?: number;
  notable?: string;
  urgency?: number;
  latency_ms?: number;
  completed_at?: string;
  error?: string;
}

interface ToolPromotionsReport {
  available?: boolean;
  scanned_at?: string;
  exec_calls_in_window?: number;
  candidate_count?: number;
  candidates?: Array<{
    shape: string;
    calls: number;
    examples: string[];
    proposed_tool_name: string;
    auto_approvable: boolean;
    destructive_seen: boolean;
  }>;
}

interface NegativeFeedbackReport {
  count: number;
  entries: Array<{
    timestamp: string;
    user_message: string;
    phrase: string;
    category: string;
    severity: number;
    preceding_assistant?: string;
  }>;
}

interface TcgSummary {
  available?: boolean;
  meta?: { last_updated?: string; total_calls?: string };
  total_top?: Array<{ member: string; weight: number }>;
  transitions_top?: Record<string, Array<{ member: string; weight: number }>>;
  hourly_top?: Record<string, Array<{ member: string; weight: number }>>;
}

interface OutcomesReport {
  aggregate?: {
    total_turns?: number;
    matched_intent_rate?: number | null;
    answered_question_rate?: number | null;
    tool_error_rate?: number | null;
  };
  recent?: Array<{
    timestamp: string;
    user_message: string;
    response_excerpt: string;
    tool_matched_intent: boolean;
    response_answered_question: boolean;
    tool_error_occurred: boolean;
    reason: string;
  }>;
  domain_tool_accuracy?: Array<{
    domain: string;
    tool: string;
    calls: number;
    successes: number;
    success_rate: number;
  }>;
}

interface TuningReport {
  last_report?: {
    started_at?: string;
    completed_at?: string;
    considered?: number;
    skipped_low_calls?: number;
    demoted?: Array<{ domain: string; tool: string; success_rate: number; calls: number }>;
    promoted_queue?: Array<{ domain: string; tool: string; success_rate: number; calls: number }>;
  } | null;
  active_overrides?: Record<string, Record<string, string>>;
  pending_promotions?: Array<{ domain: string; tool: string; success_rate: number; calls: number }>;
}

interface PhysicalState {
  state?: Record<string, any>;
  formatted_block?: string;
}

interface OperatingReviewReport {
  available?: boolean;
  narrative?: string;
  completed_at?: string;
  data?: {
    calendar?: { events_past_week?: number; events_next_week?: number };
    tasks?: { completed?: number; deferred?: number; open?: number };
    commitments?: { resolved?: number; open?: number; overdue?: number };
    biometrics?: { avg_sleep_hours?: number; total_steps_week?: number };
  };
}

interface CommitmentDrafts {
  count: number;
  drafts: Array<{
    commitment_id: string;
    channel?: string;
    to?: string;
    subject?: string;
    body?: string;
    rationale?: string;
  }>;
}

interface KnowledgeGraphReport {
  summary?: {
    available?: boolean;
    node_count?: number;
    relationship_count?: number;
    last_write?: string | null;
    top_entities?: Array<{ name: string; type: string; degree: number }>;
  };
  recent_topics?: Array<{ name: string; type: string; description: string; updated_at: string }>;
  who_to_follow_up?: Array<{ name: string; description: string; last_mentioned: string; degree: number }>;
}

interface LifeStateReport {
  current?: {
    state?: string;
    confidence?: number;
    reason?: string;
    personality_mode?: string;
    notification_threshold?: number;
    bias_domains?: string[];
    tone?: string;
    computed_at?: string;
  };
  formatted_block?: string;
  history?: Array<{ state: string; confidence: number; computed_at: string }>;
}

const STATUS_COLOR: Record<string, string> = {
  pass: '#3effa0',
  fail: '#ef4444',
  degraded: '#facc15',
  skip: '#888',
  unknown: '#888',
};

function ageString(iso?: string): string {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (!t) return '—';
  const m = Math.max(0, Math.floor((Date.now() - t) / 60000));
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function StatusDot({ status }: { status: Status }) {
  return (
    <span
      style={{
        display: 'inline-block',
        width: 8,
        height: 8,
        background: STATUS_COLOR[status] || '#888',
        marginRight: 8,
        boxShadow: `0 0 6px ${STATUS_COLOR[status] || '#888'}`,
      }}
    />
  );
}

function Panel({
  title,
  age,
  onRefresh,
  busy,
  children,
  action,
}: {
  title: string;
  age?: string;
  onRefresh?: () => void;
  busy?: boolean;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div
      className="relative bg-black/40 border border-jarvis-blue/15 px-5 py-4 mb-4"
      style={{
        clipPath:
          'polygon(10px 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%, 0 10px)',
      }}
    >
      <div className="flex items-center gap-3 mb-3 border-b border-jarvis-blue/15 pb-2">
        <div className="w-1 h-3 bg-jarvis-blue/40" />
        <h2 className="text-[11px] tracking-[0.3em] text-jarvis-blue">{title}</h2>
        <span className="text-[9px] tracking-widest text-gray-500 ml-auto">
          {age ? `LAST · ${age.toUpperCase()}` : ''}
        </span>
        {action}
        {onRefresh && (
          <button
            onClick={onRefresh}
            disabled={busy}
            className="text-[9px] tracking-widest text-jarvis-blue/70 hover:text-jarvis-blue transition disabled:opacity-30 px-2 py-0.5 border border-jarvis-blue/20"
          >
            {busy ? 'WORKING…' : 'REFRESH'}
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

function JudgeCalibrationConfirm() {
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<CalibrationConfirmResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const confirm = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const r = await api.post<CalibrationConfirmResult>('/djo-eval/calibration/confirm');
      setResult(r);
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.message || 'Request failed';
      setError(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setBusy(false);
      setArmed(false);
    }
  };

  return (
    <Panel title="JUDGE CALIBRATION (DJO-EVAL GOLD SET)">
      <div className="text-xs text-gray-400 mb-3">
        Promote the current AI-suggested gold labels to a confirmed calibration set. This is the one
        human-in-the-loop write — it stamps <span className="text-gray-200">confirmed</span> on the 40
        seed labels. Idempotent; safe to re-run.
      </div>

      {!armed && !busy && (
        <button
          onClick={() => { setArmed(true); setError(null); setResult(null); }}
          className="text-[10px] tracking-widest text-jarvis-blue/80 hover:text-jarvis-blue transition px-3 py-1 border border-jarvis-blue/30"
        >
          CONFIRM JUDGE CALIBRATION (40 LABELS)
        </button>
      )}

      {armed && !busy && (
        <div className="flex items-center gap-3">
          <span className="text-[11px] text-yellow-300">Are you sure? This confirms the 40 seed labels.</span>
          <button
            onClick={confirm}
            className="text-[10px] tracking-widest text-jarvis-blue/80 hover:text-jarvis-blue transition px-3 py-1 border border-jarvis-blue/40"
          >
            YES, CONFIRM
          </button>
          <button
            onClick={() => setArmed(false)}
            className="text-[10px] tracking-widest text-gray-400 hover:text-gray-200 transition px-3 py-1 border border-jarvis-blue/15"
          >
            CANCEL
          </button>
        </div>
      )}

      {busy && (
        <div className="text-[11px] tracking-widest text-jarvis-blue/70">CONFIRMING…</div>
      )}

      {result && (
        <div className="text-xs mt-3">
          {result.confirmed ? (
            <div style={{ color: STATUS_COLOR.pass }}>
              Confirmed {result.n} label{result.n === 1 ? '' : 's'} ✓
              {result.confirmed_by && (
                <span className="text-gray-500 ml-2">by {result.confirmed_by}</span>
              )}
            </div>
          ) : (
            <div style={{ color: STATUS_COLOR.degraded }}>
              Nothing confirmed (n={result.n ?? 0}).{' '}
              {result.status === 'disabled'
                ? 'DJO_EVAL is disabled.'
                : 'Seed the gold set first, then re-run.'}
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="text-xs text-red-400 mt-3">error: {error}</div>
      )}
    </Panel>
  );
}

export default function DiagnosticsView() {
  const [selfHealth, setSelfHealth] = useState<SelfHealth | null>(null);
  const [probes, setProbes] = useState<ProbeReport | null>(null);
  const [persona, setPersona] = useState<PersonaReport | null>(null);
  const [routing, setRouting] = useState<RoutingResponse | null>(null);
  const [sentinel, setSentinel] = useState<SentinelCalibration | null>(null);
  const [vision, setVision] = useState<VisionReading | null>(null);
  const [promotions, setPromotions] = useState<ToolPromotionsReport | null>(null);
  const [feedback, setFeedback] = useState<NegativeFeedbackReport | null>(null);
  const [tcg, setTcg] = useState<TcgSummary | null>(null);
  const [outcomes, setOutcomes] = useState<OutcomesReport | null>(null);
  const [tuning, setTuning] = useState<TuningReport | null>(null);
  const [physical, setPhysical] = useState<PhysicalState | null>(null);
  const [lifeState, setLifeState] = useState<LifeStateReport | null>(null);
  const [graph, setGraph] = useState<KnowledgeGraphReport | null>(null);
  const [review, setReview] = useState<OperatingReviewReport | null>(null);
  const [commitments, setCommitments] = useState<CommitmentDrafts | null>(null);

  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const setBusyKey = (key: string, v: boolean) =>
    setBusy(prev => ({ ...prev, [key]: v }));

  const safeFetch = async <T,>(
    key: string,
    getter: () => Promise<T>,
    setter: (v: T) => void,
  ) => {
    setBusyKey(key, true);
    try {
      setter(await getter());
    } catch (e) {
      console.error(`obs.${key} failed`, e);
    } finally {
      setBusyKey(key, false);
    }
  };

  const reloadAll = useCallback(async () => {
    await Promise.all([
      safeFetch('selfHealth', () => api.get<SelfHealth>('/observability/self_health'), setSelfHealth),
      safeFetch('probes',     () => api.get<ProbeReport>('/observability/probes'), setProbes),
      safeFetch('persona',    () => api.get<PersonaReport>('/observability/persona'), setPersona),
      safeFetch('routing',    () => api.get<RoutingResponse>('/observability/routing'), setRouting),
      safeFetch('sentinel',   () => api.get<SentinelCalibration>('/observability/sentinel?days=7'), setSentinel),
      safeFetch('vision',     () => api.get<VisionReading>('/observability/vision'), setVision),
      safeFetch('promotions', () => api.get<ToolPromotionsReport>('/observability/tool_promotions'), setPromotions),
      safeFetch('feedback',   () => api.get<NegativeFeedbackReport>('/observability/negative_feedback?limit=20'), setFeedback),
      safeFetch('tcg',        () => api.get<TcgSummary>('/observability/tcg'), setTcg),
      safeFetch('outcomes',   () => api.get<OutcomesReport>('/observability/outcomes?limit=20'), setOutcomes),
      safeFetch('tuning',     () => api.get<TuningReport>('/observability/tuning'), setTuning),
      safeFetch('physical',   () => api.get<PhysicalState>('/observability/physical_state'), setPhysical),
      safeFetch('lifeState',  () => api.get<LifeStateReport>('/observability/life_state'), setLifeState),
      safeFetch('graph',      () => api.get<KnowledgeGraphReport>('/observability/knowledge_graph'), setGraph),
      safeFetch('review',     () => api.get<OperatingReviewReport>('/observability/operating_review'), setReview),
      safeFetch('commitments',() => api.get<CommitmentDrafts>('/observability/commitments'), setCommitments),
    ]);
  }, []);

  useEffect(() => {
    reloadAll();
  }, [reloadAll]);

  return (
    <div className="px-2">
      <div className="flex justify-end mb-2">
        <button
          onClick={reloadAll}
          className="text-[10px] tracking-widest text-jarvis-blue/70 hover:text-jarvis-blue transition px-3 py-1 border border-jarvis-blue/20"
        >
          RELOAD ALL
        </button>
      </div>

      <Panel
        title="SELF-HEALTH"
        age={ageString(selfHealth?.generated_at)}
        busy={busy.selfHealth}
        onRefresh={() => safeFetch('selfHealth', () => api.get<SelfHealth>('/observability/self_health'), setSelfHealth)}
      >
        {selfHealth ? (
          <div>
            <div className="flex items-center gap-3 mb-3">
              <StatusDot status={selfHealth.overall || 'unknown'} />
              <span className="text-base" style={{ color: STATUS_COLOR[selfHealth.overall || 'unknown'] }}>
                {(selfHealth.overall || 'unknown').toUpperCase()}
              </span>
              <span className="text-gray-300 text-sm ml-2">{selfHealth.summary}</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
              {Object.entries(selfHealth.components || {}).map(([name, c]) => (
                <div key={name} className="flex items-center gap-2 border border-jarvis-blue/10 px-2 py-1">
                  <StatusDot status={c.status} />
                  <span className="text-gray-200">{name}</span>
                  <span className="text-gray-500 ml-auto truncate text-[10px]">{c.detail}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-gray-500 text-xs">…</div>
        )}
      </Panel>

      <Panel
        title="END-TO-END PROBES"
        age={ageString(probes?.completed_at)}
        busy={busy.probes}
        onRefresh={() => safeFetch('probes', () => api.get<ProbeReport>('/observability/probes'), setProbes)}
        action={
          <button
            onClick={() => safeFetch('probes', () => api.post<ProbeReport>('/observability/probes/run'), setProbes)}
            disabled={busy.probes}
            className="text-[9px] tracking-widest text-jarvis-blue/70 hover:text-jarvis-blue transition disabled:opacity-30 px-2 py-0.5 border border-jarvis-blue/20 mr-2"
          >
            RUN NOW
          </button>
        }
      >
        {probes?.probes ? (
          <div>
            <div className="flex gap-4 text-xs mb-3 text-gray-400">
              <span style={{ color: STATUS_COLOR.pass }}>PASS {probes.pass}</span>
              <span style={{ color: STATUS_COLOR.fail }}>FAIL {probes.fail}</span>
              <span>SKIP {probes.skip}</span>
              <span className="ml-auto text-gray-500">{probes.total} probes</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
              {probes.probes.map(p => (
                <div key={p.name} className="flex items-center gap-2 border border-jarvis-blue/10 px-2 py-1">
                  <StatusDot status={p.status} />
                  <span className="text-gray-200 w-32 truncate">{p.name}</span>
                  <span className="text-gray-500 text-[10px]">{p.latency_ms ?? '—'}ms</span>
                  <span className="text-gray-500 ml-2 truncate text-[10px]">{p.detail}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-gray-500 text-xs">No probe report. Run one.</div>
        )}
      </Panel>

      <Panel
        title="PERSONA REGRESSION SUITE"
        age={ageString(persona?.completed_at)}
        busy={busy.persona}
        onRefresh={() => safeFetch('persona', () => api.get<PersonaReport>('/observability/persona'), setPersona)}
        action={
          <button
            onClick={() => safeFetch('persona', () => api.post<PersonaReport>('/observability/persona/run'), setPersona)}
            disabled={busy.persona}
            className="text-[9px] tracking-widest text-jarvis-blue/70 hover:text-jarvis-blue transition disabled:opacity-30 px-2 py-0.5 border border-jarvis-blue/20 mr-2"
          >
            RUN NOW (~60s)
          </button>
        }
      >
        {persona?.scenarios ? (
          <div>
            <div className="flex gap-4 text-xs mb-3 text-gray-400">
              <span style={{ color: STATUS_COLOR.pass }}>PASS {persona.passed}</span>
              <span style={{ color: STATUS_COLOR.fail }}>FAIL {persona.failed}</span>
              <span className="ml-auto text-gray-500">{persona.total} scenarios</span>
            </div>
            <div className="space-y-1 text-xs">
              {persona.scenarios.map(s => (
                <div key={s.id} className="border border-jarvis-blue/10 px-2 py-1">
                  <div className="flex items-center gap-2">
                    <StatusDot status={s.pass ? 'pass' : 'fail'} />
                    <span className="text-gray-200 w-44 truncate">{s.id}</span>
                    <span className="text-gray-500 text-[10px]">{s.latency_ms ?? '—'}ms</span>
                    <span className="text-gray-500 ml-2 text-[10px] truncate">
                      tools: {s.tools_fired?.length ? s.tools_fired.join(', ') : '—'}
                    </span>
                  </div>
                  {s.failures?.length ? (
                    <div className="text-[10px] text-red-400 mt-1 ml-5">{s.failures.join(' · ')}</div>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-gray-500 text-xs">No persona run yet.</div>
        )}
      </Panel>

      <Panel
        title="ROUTING CORRECTIONS (SELF-HEALING)"
        busy={busy.routing}
        onRefresh={() => safeFetch('routing', () => api.get<RoutingResponse>('/observability/routing'), setRouting)}
      >
        {routing ? (
          <div>
            <div className="text-xs text-gray-400 mb-2">
              {routing.count} active correction{routing.count === 1 ? '' : 's'} injected into the system prompt.
            </div>
            {routing.system_prompt_block ? (
              <pre className="text-[10px] text-gray-300 whitespace-pre-wrap border border-jarvis-blue/10 p-2 bg-black/60">
                {routing.system_prompt_block}
              </pre>
            ) : (
              <div className="text-xs text-gray-500">Routing is clean. No active corrections.</div>
            )}
          </div>
        ) : (
          <div className="text-gray-500 text-xs">…</div>
        )}
      </Panel>

      <Panel
        title="SENTINEL CALIBRATION (7d)"
        busy={busy.sentinel}
        onRefresh={() => safeFetch('sentinel', () => api.get<SentinelCalibration>('/observability/sentinel?days=7'), setSentinel)}
      >
        {sentinel ? (
          <div className="text-xs space-y-1">
            <div>VERDICT: <span className="text-jarvis-blue">{sentinel.verdict.toUpperCase().replace('_', ' ')}</span></div>
            <div>fires: {sentinel.fires_total} ({sentinel.fires_per_day.toFixed(2)}/day)</div>
            <div>false positives: {sentinel.false_positives} ({(sentinel.false_positive_rate * 100).toFixed(1)}%)</div>
            <div>acknowledgement rate: {(sentinel.acknowledgement_rate * 100).toFixed(1)}%</div>
            {Object.keys(sentinel.fires_by_check).length > 0 && (
              <div className="text-gray-400 text-[10px] mt-2">
                by check: {Object.entries(sentinel.fires_by_check).map(([k, v]) => `${k}=${v}`).join(' · ')}
              </div>
            )}
          </div>
        ) : (
          <div className="text-gray-500 text-xs">…</div>
        )}
      </Panel>

      <JudgeCalibrationConfirm />

      <Panel
        title="CAMERA VISION"
        age={ageString(vision?.completed_at)}
        busy={busy.vision}
        onRefresh={() => safeFetch('vision', () => api.get<VisionReading>('/observability/vision'), setVision)}
        action={
          <button
            onClick={() => safeFetch('vision', () => api.post<VisionReading>('/observability/vision/run'), setVision)}
            disabled={busy.vision}
            className="text-[9px] tracking-widest text-jarvis-blue/70 hover:text-jarvis-blue transition disabled:opacity-30 px-2 py-0.5 border border-jarvis-blue/20 mr-2"
          >
            CAPTURE NOW
          </button>
        }
      >
        {vision?.ok ? (
          <div className="text-xs space-y-1">
            <div>scene: <span className="text-gray-200">{vision.scene || '(empty)'}</span></div>
            <div>people: <span className="text-gray-200">{vision.people ?? 0}</span></div>
            <div>notable: <span className="text-gray-200">{vision.notable || '(none)'}</span></div>
            <div>urgency: <span className="text-gray-200">{vision.urgency ?? 0}/3</span></div>
            <div className="text-gray-500 text-[10px] mt-1">latency {vision.latency_ms}ms</div>
          </div>
        ) : vision?.error ? (
          <div className="text-xs text-red-400">error: {vision.error}</div>
        ) : (
          <div className="text-gray-500 text-xs">No reading yet.</div>
        )}
      </Panel>

      <Panel
        title="TOOL PROMOTION CANDIDATES (LAYER 2 → 3)"
        age={ageString(promotions?.scanned_at)}
        busy={busy.promotions}
        onRefresh={() => safeFetch('promotions', () => api.get<ToolPromotionsReport>('/observability/tool_promotions'), setPromotions)}
        action={
          <>
            <button
              onClick={() => safeFetch('promotions', () => api.post<ToolPromotionsReport>('/observability/tool_promotions/scan'), setPromotions)}
              disabled={busy.promotions}
              className="text-[9px] tracking-widest text-jarvis-blue/70 hover:text-jarvis-blue transition disabled:opacity-30 px-2 py-0.5 border border-jarvis-blue/20 mr-2"
            >
              SCAN
            </button>
            <button
              onClick={() => safeFetch('promotions', () => api.post<ToolPromotionsReport>('/observability/tool_promotions/dispatch'), setPromotions)}
              disabled={busy.promotions}
              className="text-[9px] tracking-widest text-jarvis-blue/70 hover:text-jarvis-blue transition disabled:opacity-30 px-2 py-0.5 border border-jarvis-blue/20 mr-2"
            >
              DISPATCH
            </button>
          </>
        }
      >
        {promotions?.candidates?.length ? (
          <div className="text-xs space-y-1">
            <div className="text-gray-400 mb-1">
              {promotions.candidate_count} candidates from {promotions.exec_calls_in_window} mac_mini_exec calls
            </div>
            {promotions.candidates.map(c => (
              <div key={c.shape} className="border border-jarvis-blue/10 px-2 py-1">
                <div className="flex items-center gap-2">
                  <StatusDot status={c.auto_approvable ? 'pass' : 'degraded'} />
                  <span className="text-gray-200 w-44 truncate">{c.shape}</span>
                  <span className="text-gray-500 text-[10px]">{c.calls} calls</span>
                  <span className="ml-auto text-jarvis-blue/80 text-[10px]">→ {c.proposed_tool_name}</span>
                </div>
                {c.examples?.length ? (
                  <div className="text-[10px] text-gray-500 mt-1 ml-5 truncate">e.g. {c.examples[0]}</div>
                ) : null}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-xs text-gray-500">
            No candidates yet. JARVIS hasn't been using mac_mini_exec enough to identify patterns.
          </div>
        )}
      </Panel>

      <Panel
        title="OUTCOME VALIDATOR (PHASE 3)"
        busy={busy.outcomes}
        onRefresh={() => safeFetch('outcomes', () => api.get<OutcomesReport>('/observability/outcomes?limit=20'), setOutcomes)}
      >
        {outcomes?.aggregate && (outcomes.aggregate.total_turns || 0) > 0 ? (
          <div className="text-xs space-y-2">
            <div className="flex gap-4 text-gray-300">
              <span>turns: {outcomes.aggregate.total_turns}</span>
              <span style={{ color: STATUS_COLOR.pass }}>
                intent: {Math.round((outcomes.aggregate.matched_intent_rate || 0) * 100)}%
              </span>
              <span style={{ color: STATUS_COLOR.pass }}>
                answered: {Math.round((outcomes.aggregate.answered_question_rate || 0) * 100)}%
              </span>
              <span style={{ color: STATUS_COLOR.fail }}>
                errors: {Math.round((outcomes.aggregate.tool_error_rate || 0) * 100)}%
              </span>
            </div>
            {outcomes.domain_tool_accuracy && outcomes.domain_tool_accuracy.length > 0 && (
              <div>
                <div className="text-[9px] tracking-widest text-gray-500 mb-1 mt-2">
                  DOMAIN × TOOL ACCURACY (≥5 calls)
                </div>
                {outcomes.domain_tool_accuracy.slice(0, 8).map((r) => (
                  <div key={`${r.domain}-${r.tool}`} className="flex items-center gap-2 text-[10px]">
                    <span className="w-32 truncate text-gray-400">{r.domain}</span>
                    <span className="w-40 truncate text-gray-200">{r.tool}</span>
                    <span className="w-12 text-gray-500">{r.calls}</span>
                    <span style={{ color: r.success_rate >= 0.7 ? STATUS_COLOR.pass : r.success_rate < 0.4 ? STATUS_COLOR.fail : STATUS_COLOR.degraded }}>
                      {Math.round(r.success_rate * 100)}%
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="text-xs text-gray-500">No turn data yet. Use JARVIS to populate.</div>
        )}
      </Panel>

      <Panel
        title="ROUTING TUNER (PHASE 4 — AUTONOMOUS)"
        busy={busy.tuning}
        onRefresh={() => safeFetch('tuning', () => api.get<TuningReport>('/observability/tuning'), setTuning)}
        action={
          <button
            onClick={() => safeFetch('tuning', () => api.post<TuningReport>('/observability/tuning/run'), setTuning)}
            disabled={busy.tuning}
            className="text-[9px] tracking-widest text-jarvis-blue/70 hover:text-jarvis-blue transition disabled:opacity-30 px-2 py-0.5 border border-jarvis-blue/20 mr-2"
          >
            RUN NOW
          </button>
        }
      >
        {tuning ? (
          <div className="text-xs space-y-2">
            {tuning.last_report ? (
              <div className="text-gray-400 text-[10px]">
                last cycle: considered {tuning.last_report.considered}, demoted {tuning.last_report.demoted?.length || 0}, queued {tuning.last_report.promoted_queue?.length || 0}
              </div>
            ) : (
              <div className="text-gray-500">No cycle has run yet.</div>
            )}
            {tuning.active_overrides && Object.keys(tuning.active_overrides).length > 0 && (
              <div>
                <div className="text-[9px] tracking-widest text-gray-500 mb-1">
                  ACTIVE LEARNED OVERRIDES
                </div>
                {Object.entries(tuning.active_overrides).map(([d, tools]) => (
                  <div key={d} className="text-[10px] text-gray-300 mb-1">
                    <span className="text-jarvis-blue/90">{d}:</span>{' '}
                    {Object.entries(tools).map(([t, s]) => (
                      <span key={t} className="mr-2" style={{ color: s === 'demoted' ? STATUS_COLOR.fail : STATUS_COLOR.pass }}>
                        {s === 'demoted' ? '−' : '+'}{t}
                      </span>
                    ))}
                  </div>
                ))}
              </div>
            )}
            {tuning.pending_promotions && tuning.pending_promotions.length > 0 && (
              <div>
                <div className="text-[9px] tracking-widest text-gray-500 mb-1">
                  PENDING PROMOTIONS (review + approve)
                </div>
                {tuning.pending_promotions.map((p) => (
                  <div key={`${p.domain}-${p.tool}`} className="flex items-center gap-2 text-[10px]">
                    <span className="text-gray-400 w-24 truncate">{p.domain}</span>
                    <span className="text-gray-200 w-32 truncate">{p.tool}</span>
                    <span className="text-gray-500">{p.calls} calls</span>
                    <span style={{ color: STATUS_COLOR.pass }}>{Math.round(p.success_rate * 100)}%</span>
                    <button
                      onClick={() => api.post(`/observability/tuning/approve?domain=${p.domain}&tool=${p.tool}`).then(() => safeFetch('tuning', () => api.get<TuningReport>('/observability/tuning'), setTuning))}
                      className="ml-auto px-2 py-0.5 border border-jarvis-blue/30 text-jarvis-blue/80 hover:text-jarvis-blue text-[9px] tracking-widest"
                    >
                      APPROVE
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="text-xs text-gray-500">Loading…</div>
        )}
      </Panel>

      <Panel
        title="OPERATING REVIEW (REVOLUTION #4)"
        age={ageString(review?.completed_at)}
        busy={busy.review}
        onRefresh={() => safeFetch('review', () => api.get<OperatingReviewReport>('/observability/operating_review'), setReview)}
        action={
          <button
            onClick={() => safeFetch('review', () => api.post<OperatingReviewReport>('/observability/operating_review/run'), setReview)}
            disabled={busy.review}
            className="text-[9px] tracking-widest text-jarvis-blue/70 hover:text-jarvis-blue transition disabled:opacity-30 px-2 py-0.5 border border-jarvis-blue/20 mr-2"
          >
            COMPOSE NOW
          </button>
        }
      >
        {review?.narrative ? (
          <div className="text-xs space-y-3">
            {review.data && (
              <div className="flex flex-wrap gap-3 text-[10px] text-gray-400">
                {review.data.calendar?.events_past_week !== undefined && (
                  <span>events: {review.data.calendar.events_past_week}</span>
                )}
                {review.data.tasks?.completed !== undefined && (
                  <span>tasks done: {review.data.tasks.completed} / open: {review.data.tasks.open}</span>
                )}
                {review.data.commitments && (
                  <span>commitments: {review.data.commitments.resolved} resolved · {review.data.commitments.open} open · {review.data.commitments.overdue} overdue</span>
                )}
                {review.data.biometrics?.avg_sleep_hours !== undefined && (
                  <span>avg sleep: {review.data.biometrics.avg_sleep_hours}h</span>
                )}
              </div>
            )}
            <div className="text-[12px] text-gray-200 leading-relaxed whitespace-pre-wrap">
              {review.narrative}
            </div>
          </div>
        ) : (
          <div className="text-xs text-gray-500">
            No review yet. Click COMPOSE NOW for an on-demand weekly review (Sunday 21:00 MTN auto).
          </div>
        )}
      </Panel>

      <Panel
        title="COMMITMENT DRAFTS (REVOLUTION #5)"
        busy={busy.commitments}
        onRefresh={() => safeFetch('commitments', () => api.get<CommitmentDrafts>('/observability/commitments'), setCommitments)}
        action={
          <button
            onClick={() => safeFetch('commitments', () => api.post<CommitmentDrafts>('/observability/commitments/scan'), setCommitments)}
            disabled={busy.commitments}
            className="text-[9px] tracking-widest text-jarvis-blue/70 hover:text-jarvis-blue transition disabled:opacity-30 px-2 py-0.5 border border-jarvis-blue/20 mr-2"
          >
            SCAN NOW
          </button>
        }
      >
        {commitments && commitments.count > 0 ? (
          <div className="text-xs space-y-2">
            <div className="text-gray-400">
              {commitments.count} draft{commitments.count === 1 ? '' : 's'} queued. JARVIS never sends these — review and act yourself.
            </div>
            {commitments.drafts.map((d) => (
              <div key={d.commitment_id} className="border border-jarvis-blue/15 p-2">
                <div className="flex items-center gap-2 text-[10px] text-gray-500 mb-1">
                  <span className="text-jarvis-blue/80">{d.channel || 'msg'}</span>
                  <span>→ {d.to || '(unknown)'}</span>
                  <button
                    onClick={() =>
                      api.post(`/observability/commitments/clear?commitment_id=${d.commitment_id}`)
                        .then(() => safeFetch('commitments', () => api.get<CommitmentDrafts>('/observability/commitments'), setCommitments))
                    }
                    className="ml-auto text-[9px] tracking-widest text-jarvis-blue/60 hover:text-jarvis-blue px-2 py-0.5 border border-jarvis-blue/20"
                  >
                    DISMISS
                  </button>
                </div>
                {d.subject && (
                  <div className="text-[11px] text-gray-300 mb-1">subject: {d.subject}</div>
                )}
                <div className="text-[11px] text-gray-200 whitespace-pre-wrap">{d.body}</div>
                {d.rationale && (
                  <div className="text-[9px] text-gray-500 mt-1">{d.rationale}</div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-xs text-gray-500">
            No commitments approaching due-date right now. Scanner runs every 4h.
          </div>
        )}
      </Panel>

      <Panel
        title="LIVING KNOWLEDGE GRAPH (REVOLUTION #3)"
        busy={busy.graph}
        onRefresh={() => safeFetch('graph', () => api.get<KnowledgeGraphReport>('/observability/knowledge_graph'), setGraph)}
      >
        {graph?.summary?.available ? (
          <div className="text-xs space-y-3">
            <div className="flex gap-4 text-gray-300">
              <span>nodes: {graph.summary.node_count}</span>
              <span>edges: {graph.summary.relationship_count}</span>
              {graph.summary.last_write && (
                <span className="text-gray-500 ml-auto text-[10px]">
                  last write: {graph.summary.last_write.slice(0, 16).replace('T', ' ')}
                </span>
              )}
            </div>
            {graph.summary.top_entities && graph.summary.top_entities.length > 0 && (
              <div>
                <div className="text-[9px] tracking-widest text-gray-500 mb-1">
                  TOP ENTITIES BY DEGREE
                </div>
                {graph.summary.top_entities.map((e) => (
                  <div key={e.name} className="flex items-center gap-2 text-[10px]">
                    <span className="text-gray-400 w-12 truncate">{e.type}</span>
                    <span className="text-gray-200 flex-1 truncate">{e.name}</span>
                    <span className="text-jarvis-blue/80">{e.degree}</span>
                  </div>
                ))}
              </div>
            )}
            {graph.recent_topics && graph.recent_topics.length > 0 && (
              <div>
                <div className="text-[9px] tracking-widest text-gray-500 mb-1">
                  ACTIVE TOPICS (7d)
                </div>
                {graph.recent_topics.slice(0, 6).map((t) => (
                  <div key={t.name} className="text-[10px] text-gray-300 truncate">
                    • {t.name}
                  </div>
                ))}
              </div>
            )}
            {graph.who_to_follow_up && graph.who_to_follow_up.length > 0 && (
              <div>
                <div className="text-[9px] tracking-widest text-gray-500 mb-1">
                  WHO TO FOLLOW UP (≥14d STALE)
                </div>
                {graph.who_to_follow_up.slice(0, 5).map((p) => (
                  <div key={p.name} className="text-[10px] text-gray-300 truncate">
                    • {p.name}
                    {p.last_mentioned && (
                      <span className="text-gray-500 ml-2">{p.last_mentioned.slice(0, 10)}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="text-xs text-gray-500">
            Neo4j is offline. To turn on the knowledge graph, follow{' '}
            <code className="text-jarvis-blue/70">memory/neo4j-aura-setup.md</code>{' '}
            (3 Railway env vars). Until then, this panel stays empty and the chat path
            silently no-ops on graph writes.
          </div>
        )}
      </Panel>

      <Panel
        title="LIFE STATE MACHINE (REVOLUTION #2)"
        busy={busy.lifeState}
        onRefresh={() => safeFetch('lifeState', () => api.get<LifeStateReport>('/observability/life_state'), setLifeState)}
      >
        {lifeState?.current?.state ? (
          <div className="text-xs space-y-2">
            <div className="flex items-center gap-3">
              <span className="text-jarvis-blue text-base font-semibold tracking-widest">
                {lifeState.current.state}
              </span>
              <span className="text-gray-500 text-[10px]">
                conf {Math.round((lifeState.current.confidence || 0) * 100)}%
              </span>
              <span className="text-gray-400 ml-auto text-[10px]">
                {lifeState.current.personality_mode || 'default'} · notify ≥ {lifeState.current.notification_threshold ?? 1}
              </span>
            </div>
            {lifeState.current.tone && (
              <div className="text-[11px] text-gray-300">{lifeState.current.tone}</div>
            )}
            {lifeState.current.reason && (
              <div className="text-[10px] text-gray-500">why: {lifeState.current.reason}</div>
            )}
            {lifeState.history && lifeState.history.length > 1 && (
              <div className="text-[10px] text-gray-500 mt-2">
                recent: {lifeState.history.slice(0, 6).map((h) => h.state).join(' → ')}
              </div>
            )}
          </div>
        ) : (
          <div className="text-xs text-gray-500">
            Life state UNKNOWN — sensor fusion + calendar haven't yet provided enough signal to lock in a state.
          </div>
        )}
      </Panel>

      <Panel
        title="SENSOR FUSION (PHYSICAL STATE)"
        busy={busy.physical}
        onRefresh={() => safeFetch('physical', () => api.get<PhysicalState>('/observability/physical_state'), setPhysical)}
      >
        {physical?.formatted_block ? (
          <pre className="text-[10px] text-gray-300 whitespace-pre-wrap border border-jarvis-blue/10 p-2 bg-black/60">
            {physical.formatted_block}
          </pre>
        ) : (
          <div className="text-xs text-gray-500">
            Sensor fusion is quiet right now (no biometric / location / camera data). The block is conditional — it only renders when there is something material to add.
          </div>
        )}
      </Panel>

      <Panel
        title="TOOL CALL GRAPH (PHASE 2)"
        busy={busy.tcg}
        onRefresh={() => safeFetch('tcg', () => api.get<TcgSummary>('/observability/tcg'), setTcg)}
      >
        {tcg?.total_top?.length ? (
          <div className="text-xs space-y-3">
            <div className="text-gray-400">
              {tcg.meta?.total_calls ?? '0'} total calls observed.
            </div>
            <div>
              <div className="text-[9px] tracking-widest text-gray-500 mb-1">
                MOST CALLED TOOLS
              </div>
              {tcg.total_top.slice(0, 8).map((t) => (
                <div key={t.member} className="flex items-center gap-2">
                  <span className="text-gray-200 w-44 truncate">{t.member}</span>
                  <span className="text-jarvis-blue/80 text-[10px]">{t.weight}</span>
                </div>
              ))}
            </div>
            {tcg.transitions_top &&
              Object.keys(tcg.transitions_top).length > 0 && (
                <div>
                  <div className="text-[9px] tracking-widest text-gray-500 mb-1">
                    TOP TRANSITIONS
                  </div>
                  {Object.entries(tcg.transitions_top)
                    .slice(0, 4)
                    .map(([from, rows]) => (
                      <div key={from} className="text-[10px] mb-1">
                        <span className="text-jarvis-blue/90">{from}</span>
                        <span className="text-gray-500"> → </span>
                        <span className="text-gray-300">
                          {rows
                            .slice(0, 3)
                            .map((r) => `${r.member} (${r.weight})`)
                            .join(', ')}
                        </span>
                      </div>
                    ))}
                </div>
              )}
          </div>
        ) : (
          <div className="text-xs text-gray-500">
            Graph is empty. Use JARVIS for a few turns and the graph will populate.
          </div>
        )}
      </Panel>

      <Panel
        title="NEGATIVE FEEDBACK FLAGS"
        busy={busy.feedback}
        onRefresh={() => safeFetch('feedback', () => api.get<NegativeFeedbackReport>('/observability/negative_feedback?limit=20'), setFeedback)}
      >
        {feedback?.entries?.length ? (
          <div className="text-xs space-y-1">
            <div className="text-gray-400 mb-1">{feedback.count} recent flagged exchange{feedback.count === 1 ? '' : 's'}</div>
            {feedback.entries.map((e, i) => (
              <div key={i} className="border border-jarvis-blue/10 px-2 py-1">
                <div className="flex items-center gap-2 text-[10px]">
                  <span className="text-red-400">{e.category}/{e.severity}</span>
                  <span className="text-gray-500">{ageString(e.timestamp)}</span>
                  <span className="text-gray-300 ml-2 truncate">"{e.user_message}"</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-xs text-gray-500">No negative feedback flagged. JARVIS is behaving.</div>
        )}
      </Panel>
    </div>
  );
}
